import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from 'react';

// Components that always load (used on all pages)
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import PrivateRoute from "./components/PrivateRoute";
import PageLoader from "./components/PageLoader";
import IframeProtection from "./components/IframeProtection";
import ErrorBoundary from "./components/ErrorBoundary";

import "./App.css";
import './styles/globals.css';
import { MobileOverrides } from './styles/mobileOverrides';

import { AuthProvider } from "./context/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setupHoverPrefetch, prefetchLikelyRoutes } from "./utils/prefetch";
import chartCache from "./utils/chartCache";

// ============================================
// LAZY LOADING - Pages load on demand
// This reduces initial bundle size by ~70%
// ============================================
const AvaliacaoGoverno = lazy(() => import("./pages/AvaliacaoGoverno/index"));
const AprovacaoGoverno = lazy(() => import("./pages/AprovacaoGoverno/index"));
const Comparativo = lazy(() => import("./pages/Comparativo/index"));
const Indices = lazy(() => import("./pages/Indices/index"));
const Eleicao = lazy(() => import("./pages/Eleicao/index"));
const Dashboard = lazy(() => import("./pages/Dashboard/index"));
const Login = lazy(() => import("./pages/Login/index"));
const Entenda = lazy(() => import("./pages/Entenda"));
const Manutencao = lazy(() => import("./pages/Manutencao/index"));

// Inner component to use hooks that require Router context
function AppContent() {
  const location = useLocation();

  // Setup hover prefetching once
  useEffect(() => {
    setupHoverPrefetch();
  }, []);

  // Make cache available globally for debugging
  useEffect(() => {
    window.chartCache = chartCache;
  }, []);

  // Handle route changes
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
    
    // Clear any pending navigation timeouts
    if (window.navigationTimeout) {
      clearTimeout(window.navigationTimeout);
      window.navigationTimeout = null;
    }
    
    // Prefetch likely routes
    prefetchLikelyRoutes(location.pathname);
    
    // Force memory cleanup on route change
    if (window.chartCache) {
      window.chartCache.clearAll?.();
    }
  }, [location.pathname]);

  return (
    <>
      <MobileOverrides />
      <Navbar />
      <div className="app-wrapper">
        <div className="layout-container">
          {/* Suspense wraps all lazy-loaded routes */}
          <ErrorBoundary key={location.key}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                  <Route path="/" element={<Eleicao />} />
                  <Route path="/avaliacao" element={<AvaliacaoGoverno />} />
                  <Route path="/aprovacao" element={<AprovacaoGoverno />} />
                  <Route path="/manutencao" element={<Manutencao />} />
                  <Route path="/comparativo" element={<Comparativo />} />
                  <Route path="/indices" element={<Indices />} />
                  <Route path="/eleicao" element={<Navigate to="/" replace />} />
                  <Route path="/entenda" element={<Entenda />} />
                  <Route 
                    path="/login" 
                    element={
                      <IframeProtection redirectTo="/">
                        <Login />
                      </IframeProtection>
                    } 
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <IframeProtection redirectTo="/">
                        <PrivateRoute>
                          <Dashboard />
                        </PrivateRoute>
                      </IframeProtection>
                    }
                  />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </div>
        <Footer />
      </div>
      <ToastContainer 
        position="top-right"
        autoClose={3000} 
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
